import React, { useEffect, useId, useState } from 'react';
import DsButton from '../../DsButton';
import DsModal from '../../DsModal';
import ExplanationButton from '../../ExplanationButton';
import ChatHeaderOverflowMenu from '../shared/ChatHeaderOverflowMenu';
import RawDsSelect from '../../DsSelect';
import { listChatTopicEvents } from '../../../../api/chatTopics';
import { sidebarTimeLabelJST } from '../../../../utils/datetime';

const DsSelect = RawDsSelect as React.ComponentType<React.SelectHTMLAttributes<HTMLSelectElement>>;

export function TopicSummary({ topic, onSelect, selected = false }: { topic: any; onSelect: (topic: any) => void; selected?: boolean }) {
  const last = topic.lastMessage;
  const preview = last?.messageFormat?.includes('image') ? '画像' : last?.messageFormat?.includes('file') ? 'ファイル' : last?.content || 'まだメッセージはありません';
  return <DsButton variant="ghost" className="chat-topic-summary" onClick={() => onSelect(topic)} aria-current={selected ? 'true' : undefined}>
    <span className="chat-topic-summary__heading"><span>{topic.name}</span>{selected && <i className="fa-solid fa-check chat-topic-current" aria-hidden="true" />}{topic.unreadCount > 0 && <span className="chat-topic-unread" aria-label={`未読 ${topic.unreadCount} 件`}>{topic.unreadCount}</span>}</span>
    <span className="chat-topic-summary__detail"><span>{preview}</span><time>{last?.createdAt ? sidebarTimeLabelJST(last.createdAt) : ''}</time></span>
  </DsButton>;
}

export default function ChatTopicModal({ topics }: { topics: any }) {
  const formId = useId();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('named');
  const [event, setEvent] = useState<any>(null);
  const [eventQuery, setEventQuery] = useState('');
  const [events, setEvents] = useState<any[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [renaming, setRenaming] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setCreating(false); setRenaming(null); setError('');
  }, [topics?.key, topics?.open]);
  useEffect(() => {
    if (!topics?.open || !creating || kind !== 'event') return undefined;
    let alive = true;
    const timer = setTimeout(() => {
      listChatTopicEvents({ actorType: topics.role, scope: topics.scope, conversationId: topics.conversationId, q: eventQuery, limit: 20 })
        .then((data) => { if (alive) setEvents(data.events || []); })
        .catch((failure) => { if (alive) setError(failure.message); });
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [creating, eventQuery, kind, topics?.conversationId, topics?.open, topics?.scope]);
  if (!topics?.enabled) return null;
  const run = async (action: () => Promise<void>) => { setBusy(true); setError(''); try { await action(); } catch (failure: any) { setError(failure.message); } finally { setBusy(false); } };
  const create = () => {
    if (kind === 'named' && (!name.trim() || [...name.trim()].length > 80 || /[\r\n\u0000-\u001f]/.test(name))) { setError('スレッド名を1〜80文字で入力してください。'); return; }
    if (kind === 'event' && !event) { setError('イベントを選択してください。'); return; }
    topics.selectTopic({ clientId: crypto.randomUUID(), kind, name: kind === 'event' ? event.name : name.trim(), ...(event ? { eventId: event.id } : {}) });
  };
  const rows = topics.rows.filter((topic: any) => (showHidden || !topic.hidden));
  return <>
    <DsModal open={topics.invitation && !topics.open} title="スレッドに新着メッセージ" ariaLabel="スレッドに新着メッセージ" onClose={topics.dismissInvitation} closeOnBack={false} footer={<><DsButton variant="secondary" onClick={topics.dismissInvitation}>すべてのメッセージを見る</DsButton><DsButton onClick={topics.acceptInvitation}>はい、スレッドを開く</DsButton></>}>
      <p>スレッドでメッセージが来ています。スレッドを開きますか？</p>
      <p className="chat-topic-help">スレッドは話題ごとの絞り込みです。開いたスレッドだけが既読になります。</p>
    </DsModal>
    <DsModal ariaLabel="スレッド" open={topics.open}
      title={creating ? '新しいスレッド' : renaming ? 'スレッド名を変更' : 'スレッド'}
      onClose={topics.closePicker} size="md" bodyClassName="chat-topic-picker"
      headerActions={<ExplanationButton title="スレッドの使い方" ariaLabel="スレッドの使い方" variant="modal"
        body="話題ごとに投稿をまとめられます。スレッド内から送るとその話題への返信になり、「すべてのメッセージ」にも表示されます。">{null}</ExplanationButton>}
      footer={creating || renaming ? <div className="chat-topic-footer-actions">
        <DsButton variant="secondary" onClick={() => { setCreating(false); setRenaming(null); setError(''); }}>一覧に戻る</DsButton>
        <DsButton type="submit" form={formId} variant={renaming ? 'modify' : 'create'} loading={busy}
          disabled={renaming ? !name.trim() : kind === 'named' ? !name.trim() : !event}>
          {renaming ? '変更を保存' : '入力へ進む'}
        </DsButton>
      </div> : null}>
      {(error || topics.error) && <p className="chat-topic-error" role="alert">{error || topics.error}</p>}
      {renaming ? <form id={formId} className="chat-topic-form" onSubmit={(e) => { e.preventDefault(); run(async () => { await topics.rename(renaming, name.trim()); setRenaming(null); }); }}>
        <label className="chat-topic-field">スレッド名<input className="input" autoFocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} /></label>
      </form> : creating ? <form id={formId} className="chat-topic-form" onSubmit={(e) => { e.preventDefault(); create(); }}>
        <label className="chat-topic-field">スレッドの種類
          <DsSelect value={kind} onChange={(e) => { setKind(e.target.value); setError(''); }} aria-label="スレッドの種類">
            <option value="named">名前を付ける</option><option value="event">イベントに紐付ける</option>
          </DsSelect>
        </label>
        {kind === 'named' ? <label className="chat-topic-field">スレッド名
          <input className="input" autoFocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="例：当日の集合について" />
        </label> : <>
          <label className="chat-topic-field">イベントを検索<input className="input" value={eventQuery} onChange={(e) => setEventQuery(e.target.value)} placeholder="イベント名を入力" /></label>
          <div className="chat-topic-event-options" aria-label="イベント候補">{events.map((item) => <DsButton key={item.id} variant="ghost" aria-pressed={event?.id === item.id} className="chat-topic-event-option" onClick={() => setEvent(item)}>
            <i className="fa-regular fa-calendar" aria-hidden="true" /><span>{item.name}</span>{event?.id === item.id && <i className="fa-solid fa-check" aria-hidden="true" />}
          </DsButton>)}</div>
        </>}
        <div className="chat-topic-guidance"><i className="fa-regular fa-paper-plane" aria-hidden="true" /><p>最初の送信で作成されます。</p></div>
      </form> : <>
        <DsButton variant="ghost" className="chat-topic-all" onClick={() => topics.selectTopic(null)} aria-label="すべてのメッセージに戻る" aria-current={!topics.selected ? 'true' : undefined}>
          <strong>すべてのメッセージ</strong>
          {!topics.selected ? <i className="fa-solid fa-check chat-topic-current" aria-hidden="true" /> : <i className="fa-solid fa-arrow-right" aria-hidden="true" />}
        </DsButton>
        <div className="chat-topic-list-heading"><label className="chat-topic-filter"><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />非表示も表示</label><DsButton variant="create" size="sm" onClick={() => { setCreating(true); setName(''); setEvent(null); setError(''); }}><i className="fa-solid fa-plus" aria-hidden="true" />新しいスレッド</DsButton></div>
        <div className="chat-topic-list">
          {!topics.loaded && !topics.error && <div className="chat-topic-empty" role="status"><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /><p>読み込んでいます…</p></div>}
          {topics.error && <DsButton variant="secondary" onClick={() => topics.refresh()}>再読み込み</DsButton>}
          {topics.loaded && !rows.length && !topics.error && <div className="chat-topic-empty">
            <i className="fa-solid fa-list-check" aria-hidden="true" />
            <strong>{topics.rows.length ? '表示中のスレッドはありません' : 'まだスレッドがありません'}</strong>
          </div>}
          {rows.map((topic: any) => <div key={topic.id} className="chat-topic-item" data-selected={topics.selected?.id === topic.id || undefined}>
            <div className="chat-topic-item__main"><TopicSummary topic={topic} onSelect={topics.selectTopic} selected={topics.selected?.id === topic.id} />
              {topic.hidden && <span className="chat-topic-item__state">非表示</span>}
            </div>
            <ChatHeaderOverflowMenu className="chat-topic-item__menu" ariaLabel={`${topic.name}の操作`} iconClassName="fa-solid fa-ellipsis" disabled={busy}
              items={[
                { key: 'rename', icon: 'fa-pen', label: '名前を変更', hidden: topic.kind !== 'named', onClick: () => { setRenaming(topic); setName(topic.name); } },
                { key: 'visibility', icon: topic.hidden ? 'fa-eye' : 'fa-eye-slash', label: topic.hidden ? '一覧に再表示' : '自分の一覧から非表示', onClick: () => run(() => topics.hide(topic, !topic.hidden)) },
              ]} />
          </div>)}
          {topics.nextCursor && <DsButton variant="secondary" block onClick={() => topics.refresh({ append: true })}>さらに表示</DsButton>}
        </div>
      </>}
    </DsModal>
  </>;
}
